/**
 * École Nid Douillet - Analytics API Service
 * 
 * API service for advanced analytics and reporting
 */

import { BaseApiService } from './api';
import { ApiResponse } from '@/types';

// Analytics types
export interface EnrollmentStats {
  total_children: number;
  male_count: number;
  female_count: number;
  average_age: number;
  active_class_levels: number;
  recent_enrollments: number;
}

export interface EnrollmentTrend {
  period: string;
  enrollments: number;
  male_enrollments: number;
  female_enrollments: number;
  avg_age_at_enrollment: number;
}

export interface ClassDistribution {
  level_name: string;
  level_name_arabic: string;
  student_count: number;
  male_count: number;
  female_count: number;
  average_age: number;
  capacity: number;
  occupancy_rate: number;
}

export interface AgeDistribution {
  age: number;
  count: number;
  male_count: number;
  female_count: number;
}

export interface MonthlyPattern {
  month: number;
  month_name: string;
  enrollments: number;
  avg_monthly_enrollments: number;
}

export interface CareServiceUtilization {
  service_name: string;
  enrolled_count: number;
  unique_children: number;
  avg_monthly_fee: number;
  total_monthly_revenue: number;
}

export interface EnrollmentAnalytics {
  enrollment_stats: EnrollmentStats;
  trends: EnrollmentTrend[];
  class_distribution: ClassDistribution[];
  age_distribution: AgeDistribution[];
  monthly_patterns: MonthlyPattern[];
  care_services: CareServiceUtilization[];
  metadata: {
    timeframe: string;
    date_from?: string;
    date_to?: string;
    academic_year_id?: number;
    class_level_id?: number;
    generated_at: string;
  };
}

export interface RevenueStats {
  total_revenue: number;
  total_payments: number;
  avg_payment_amount: number;
  completed_revenue: number;
  pending_revenue: number;
  overdue_revenue: number;
  completed_payments: number;
  pending_payments: number;
  overdue_payments: number;
}

export interface RevenueTrend {
  period: string;
  revenue: number;
  payment_count: number;
  avg_payment: number;
  enrollment_fees: number;
  monthly_fees: number;
  care_service_fees: number;
  completed_count: number;
  overdue_count: number;
}

export interface PaymentType {
  payment_type: string;
  payment_count: number;
  total_amount: number;
  avg_amount: number;
  completed_count: number;
  overdue_count: number;
}

export interface ExpenseStats {
  total_expenses: number;
  total_expense_records: number;
  avg_expense_amount: number;
}

export interface ExpenseCategory {
  category: string;
  expense_count: number;
  total_amount: number;
  avg_amount: number;
}

export interface OutstandingPayments {
  overdue_count: number;
  overdue_amount: number;
  avg_days_overdue: number;
  severely_overdue: number;
}

export interface FinancialSummary {
  total_revenue: number;
  total_expenses: number;
  net_profit: number;
  profit_margin: number;
}

export interface FinancialProjection {
  projected_month: string;
  projected_revenue: number;
}

export interface FinancialAnalytics {
  revenue_stats: RevenueStats;
  revenue_trends: RevenueTrend[];
  payment_types: PaymentType[];
  expense_stats: ExpenseStats;
  expense_categories: ExpenseCategory[];
  outstanding_payments: OutstandingPayments;
  financial_summary: FinancialSummary;
  projections?: FinancialProjection[];
  metadata: {
    timeframe: string;
    date_from?: string;
    date_to?: string;
    academic_year_id?: number;
    include_projections: boolean;
    generated_at: string;
  };
}

export interface ParentEngagement {
  total_parents: number;
  email_preferred: number;
  sms_preferred: number;
  both_preferred: number;
  french_preferred: number;
  arabic_preferred: number;
  avg_children_per_parent: number;
}

export interface NotificationStats {
  total_notifications: number;
  delivered_count: number;
  failed_count: number;
  email_notifications: number;
  sms_notifications: number;
  both_notifications: number;
  delivery_rate: number;
}

export interface CareServiceUtilizationOp {
  service_name: string;
  capacity: number;
  current_enrollment: number;
  utilization_rate: number;
  monthly_fee: number;
  monthly_revenue: number;
}

export interface AcademicYearPerformance {
  year_label: string;
  start_date: string;
  end_date: string;
  total_enrolled: number;
  new_enrollments: number;
  total_revenue: number;
  total_payments: number;
}

export interface SystemUsage {
  active_days: number;
  total_activities: number;
  login_count: number;
  payment_activities: number;
  notification_activities: number;
}

export interface OperationalAnalytics {
  parent_engagement: ParentEngagement;
  notification_stats: NotificationStats;
  care_services_utilization: CareServiceUtilizationOp[];
  academic_year_performance: AcademicYearPerformance[];
  system_usage: SystemUsage;
  metadata: {
    date_from?: string;
    date_to?: string;
    academic_year_id?: number;
    generated_at: string;
  };
}

export interface Recommendation {
  category: string;
  priority: 'Haute' | 'Moyenne' | 'Basse';
  title: string;
  description: string;
  action: string;
}

export interface ComprehensiveReport {
  report_info: {
    title: string;
    generated_at: string;
    generated_by: string;
    period: {
      date_from: string;
      date_to: string;
      academic_year_id?: number;
    };
  };
  executive_summary: {
    total_children: number;
    total_revenue: number;
    net_profit: number;
    profit_margin: number;
    parent_satisfaction: number;
    system_health: string;
  };
  enrollment_analytics: EnrollmentAnalytics;
  financial_analytics: FinancialAnalytics;
  operational_analytics: OperationalAnalytics;
  recommendations: Recommendation[];
}

export interface AnalyticsQueryParams {
  academic_year_id?: number;
  date_from?: string;
  date_to?: string;
  timeframe?: 'daily' | 'weekly' | 'monthly' | 'yearly';
  class_level_id?: number;
  include_projections?: boolean;
}

export interface ReportQueryParams {
  academic_year_id?: number;
  date_from?: string;
  date_to?: string;
  format_type?: 'json' | 'pdf' | 'excel';
}

/**
 * Analytics API Service
 */
export class AnalyticsApiService extends BaseApiService {
  /**
   * Get enrollment analytics
   */
  async getEnrollmentAnalytics(params?: AnalyticsQueryParams): Promise<ApiResponse<EnrollmentAnalytics>> {
    return this.get('/analytics/enrollment', { params });
  }

  /**
   * Get financial analytics
   */
  async getFinancialAnalytics(params?: AnalyticsQueryParams): Promise<ApiResponse<FinancialAnalytics>> {
    return this.get('/analytics/financial', { params });
  }

  /**
   * Get operational analytics
   */
  async getOperationalAnalytics(params?: AnalyticsQueryParams): Promise<ApiResponse<OperationalAnalytics>> {
    return this.get('/analytics/operational', { params });
  }

  /**
   * Generate comprehensive report
   */
  async generateComprehensiveReport(params?: ReportQueryParams): Promise<ApiResponse<ComprehensiveReport>> {
    return this.get('/analytics/comprehensive-report', { params });
  }

  /**
   * Get enrollment trends for charts
   */
  async getEnrollmentTrends(params?: AnalyticsQueryParams): Promise<ApiResponse<EnrollmentTrend[]>> {
    const response = await this.getEnrollmentAnalytics(params);
    if (response.success && response.data) {
      return {
        success: true,
        data: response.data.trends,
        message: 'Tendances d\'inscription récupérées'
      };
    }
    return response as any;
  }

  /**
   * Get revenue trends for charts
   */
  async getRevenueTrends(params?: AnalyticsQueryParams): Promise<ApiResponse<RevenueTrend[]>> {
    const response = await this.getFinancialAnalytics(params);
    if (response.success && response.data) {
      return {
        success: true,
        data: response.data.revenue_trends,
        message: 'Tendances de revenus récupérées'
      };
    }
    return response as any;
  }

  /**
   * Get class distribution for charts
   */
  async getClassDistribution(params?: AnalyticsQueryParams): Promise<ApiResponse<ClassDistribution[]>> {
    const response = await this.getEnrollmentAnalytics(params);
    if (response.success && response.data) {
      return {
        success: true,
        data: response.data.class_distribution,
        message: 'Distribution des classes récupérée'
      };
    }
    return response as any;
  }

  /**
   * Get financial summary
   */
  async getFinancialSummary(params?: AnalyticsQueryParams): Promise<ApiResponse<FinancialSummary>> {
    const response = await this.getFinancialAnalytics(params);
    if (response.success && response.data) {
      return {
        success: true,
        data: response.data.financial_summary,
        message: 'Résumé financier récupéré'
      };
    }
    return response as any;
  }

  /**
   * Get care services utilization
   */
  async getCareServicesUtilization(params?: AnalyticsQueryParams): Promise<ApiResponse<CareServiceUtilizationOp[]>> {
    const response = await this.getOperationalAnalytics(params);
    if (response.success && response.data) {
      return {
        success: true,
        data: response.data.care_services_utilization,
        message: 'Utilisation des services de garde récupérée'
      };
    }
    return response as any;
  }
}

// Export singleton instance
export const analyticsApi = new AnalyticsApiService();
